export type SlideFrameId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type StructuralSlideId = "frontCover" | "sectionDivider" | "disclaimer";

export type OutlineEntry =
  | { slideType: "analytical"; slideFrameId: SlideFrameId }
  | { slideType: "structural"; slideFrameId: StructuralSlideId };

export interface OutlineFrameItem {
  id: number;
  src: string;
}

interface SlideMatcher {
  label: string;
  match: (decodedSrc: string) => OutlineEntry | null;
}

const ANALYTICAL_PATTERN = /\/slides\/Slide(\d+)\.PNG$/i;
const ANALYTICAL_FRAME_IDS = new Set<number>([1, 2, 3, 4, 5, 6, 7, 8, 9]);

const STRUCTURAL_FILENAMES: Readonly<Record<string, StructuralSlideId>> = {
  "front cover.png": "frontCover",
  "section divider.png": "sectionDivider",
  "disclaimer.png": "disclaimer",
};

const SLIDE_MATCHERS: ReadonlyArray<SlideMatcher> = [
  {
    label: "analytical (Slide{N}.PNG)",
    match: (decoded) => {
      const m = decoded.match(ANALYTICAL_PATTERN);
      if (!m) return null;
      const id = Number(m[1]);
      if (!ANALYTICAL_FRAME_IDS.has(id)) return null;
      return { slideType: "analytical", slideFrameId: id as SlideFrameId };
    },
  },
  {
    label: "structural (Front Cover / Section Divider / Disclaimer)",
    match: (decoded) => {
      const filename = (decoded.split("?")[0].split("/").pop() ?? "").toLowerCase();
      const kind = STRUCTURAL_FILENAMES[filename];
      return kind ? { slideType: "structural", slideFrameId: kind } : null;
    },
  },
];

export function srcToOutlineEntry(src: string): OutlineEntry {
  const decoded = safeDecode(src);
  for (const matcher of SLIDE_MATCHERS) {
    const entry = matcher.match(decoded);
    if (entry) return entry;
  }
  throw new Error(`Unrecognised slide source path: "${src}"`);
}

export function outlineFramesToSelectedSlides(
  frames: ReadonlyArray<OutlineFrameItem>,
): OutlineEntry[] {
  return frames.map((f) => srcToOutlineEntry(f.src));
}

function safeDecode(src: string): string {
  try {
    return decodeURIComponent(src);
  } catch {
    return src;
  }
}
